import { useEffect, useState } from 'react';
import { useApi } from './hooks/useApi';
import type { SignerOption, TemplateSigner } from './types';

/** Customer + up to two of the merchant's own signers. */
export const MAX_SIGNERS = 3;

/** One colour per signer, used for their fields in both editors. */
export const SIGNER_COLORS: Record<number, string> = {
  1: '#2563EB', // customer — blue
  2: '#16A34A', // signer 2 — green
  3: '#EA580C', // signer 3 — orange
};

export function signerColor(position: number | undefined): string {
  return SIGNER_COLORS[position ?? 1] ?? SIGNER_COLORS[1];
}

/** Customer first, then the template's signers in signing order. */
export function signerOptions(signers: TemplateSigner[] | undefined): SignerOption[] {
  return [
    { position: 1, label: 'Customer' },
    ...(signers ?? []).map((s) => ({ position: s.position, label: s.name || `Signer ${s.position}` })),
  ];
}

export interface MultiSignerAccess {
  /** Show the feature (every store; Pro sees an upgrade prompt). */
  visible: boolean;
  /** The plan includes it — Plus or Business (may set it up). */
  available: boolean;
}

/** Whether this store may see / use multiple signers (from /auth/billing-status). */
export function useMultiSignerAccess(): MultiSignerAccess | null {
  const { request } = useApi();
  const [access, setAccess] = useState<MultiSignerAccess | null>(null);
  useEffect(() => {
    request<{ multiSigner?: MultiSignerAccess }>('/auth/billing-status')
      .then((b) => setAccess(b.multiSigner ?? { visible: false, available: false }))
      .catch(() => setAccess({ visible: false, available: false }));
  }, [request]);
  return access;
}
